import {
  findNodes,
  removeNodesFromTree,
  updateMatchingNodesInTree,
} from '../../../components/Tree/helpers/helpers';
import {
  NODE_TYPE_CATEGORY_GROUP,
  NODE_TYPE_MULTI_SELECT,
} from '../../../utils/types/nodeTypes';
import { selectedTreeNodesSelector } from '../../selectors/tree/node';
import { selectTreeDataByTreeId, updateData } from '../../slices/tree';
import { resetSelectedNode } from '../../slices/treeSelection';
import type { AppThunkDispatch, RootState } from '../..';
import type { NodeBaseItem } from '../../../entities/Node';

export function getItemCategoryRecommendationNodeItems(
  nodeItem: NodeBaseItem,
  getState: () => RootState,
): NodeBaseItem[] {
  if (nodeItem.node.type !== NODE_TYPE_MULTI_SELECT) {
    return [nodeItem];
  }

  const { nodeItems } = selectedTreeNodesSelector(getState());
  return nodeItems.filter(
    (ni: NodeBaseItem) => ni.node.isItemCategoryRecommendation,
  );
}

export const removeItemCategoryRecommendationNodes =
  (tree: string, nodeItems: NodeBaseItem[]) =>
  (dispatch: AppThunkDispatch, getState: () => RootState) => {
    if (!nodeItems.length) return;

    const treeData = selectTreeDataByTreeId(getState(), tree);
    let updatedTreeData = removeNodesFromTree({
      paths: nodeItems.map((ni) => ni.path),
      treeData,
    }).treeData;

    const removedCountByParentId = nodeItems.reduce<Record<string, number>>(
      (counts, ni) => {
        counts[ni.node.parentId] = (counts[ni.node.parentId] || 0) + 1;
        return counts;
      },
      {},
    );

    Object.entries(removedCountByParentId).forEach(([parentId, removedCount]) => {
      updatedTreeData = updateMatchingNodesInTree(
        (ni: NodeBaseItem) => ni.node.id === parentId,
        updatedTreeData,
        (ni: NodeBaseItem) => ({
          group: {
            childItemCount: Math.max(
              0,
              (ni.node.group?.childItemCount ?? 0) - removedCount,
            ),
          },
        }),
      );
    });

    const emptyGroupPaths = (
      findNodes({
        treeData: updatedTreeData,
        searchMethod: (ni: NodeBaseItem) =>
          (ni.node.type === NODE_TYPE_CATEGORY_GROUP && !ni.node.children?.length) ||
          (ni.node.type === NODE_TYPE_MULTI_SELECT && !ni.node.children?.length),
        searchFocusOffset: 0,
        searchQuery: null,
      }) as unknown as { matches: NodeBaseItem[] }
    ).matches.map((m) => m.path);

    if (emptyGroupPaths.length) {
      updatedTreeData = removeNodesFromTree({
        paths: emptyGroupPaths,
        treeData: updatedTreeData,
      }).treeData;
    }
    dispatch(updateData(tree, updatedTreeData));
    dispatch(resetSelectedNode());
  };
